var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MediaStreamTransmissionStartedMessage, ModelMediaStreamStartedMessage, ModelMediaStreamEndedMessage, RequestStartScreenStreamMessage, RequestStopScreenStreamMessage, RequestStartPlayer3DViewSharingMessage, RequestStopPlayer3DViewSharingMessage } from "../../../messages/media";
import { peerService } from "./PeerService";
import { PeerAddedMessage, PeerRemovedMessage } from "../../../messages/session/PeerMessages";
import { busService } from "../../bus";
import { MulticastDelegate, RoomChangedMessage } from "flavr-common";
import { usersService } from "../UsersService";
import { renderer } from "../../rendering/RendererGlobals";
import { webcamBackgroundService } from "../../webcam/WebcamBackgroundService";
import { preferencesService } from "../../preferences/PreferencesService";
import { useMediaDevicesStore } from "../../../ui/store/media-devices.store";
import { adaptiveBandwidthService } from "./AdaptiveBandwidthService";
import { capabilitiesService } from "../../rendering/CapabilitiesService";
import { MessageService } from "../../../ui/services/message.service";
import i18next from "i18next";
import { configureWebcamBackground, configureWebcamSegmentation, getWebcamBackgroundMode } from "../../webcam/WebcamBackgroundSettings";
export class LocalStreamingService {
    constructor() {
        this.onUpdateDelegate = new MulticastDelegate();
        this.peerMgr = null;
        this.numberOfTriesToInitialize = 0;
        this.backgroundUpdate = Promise.resolve();
        this.backgroundUpdateRevision = 0;
        this.connectedPeerIds = new Set();
        this.bus = busService.connection("LocalStreamingService");
        this.initialized = false;
        this.player3DViewStreams = [];
        this.screenStreams = [];
    }
    init() {
        this.numberOfTriesToInitialize = 0;
        // PeerService / PeerManager might not be initialized yet, so we do the initialization in a sub-function that can be repeated until things are available
        const doInit = () => {
            if ((peerService === null || peerService === void 0 ? void 0 : peerService.peerMgr) != null) {
                this.peerMgr = peerService.peerMgr;
                this.initialized = true;
                this.bus.subscribeByType(PeerAddedMessage, (msg) => {
                    this.sendStreamsToPeer(msg.clientId);
                    if (!this.connectedPeerIds.has(msg.clientId)) {
                        this.connectedPeerIds.add(msg.clientId);
                        this.onUpdateDelegate.invoke();
                    }
                });
                this.bus.subscribeByType(PeerRemovedMessage, (msg) => {
                    this.stopStreamsToPeer(msg.clientId);
                    if (this.connectedPeerIds.delete(msg.clientId)) {
                        this.onUpdateDelegate.invoke();
                    }
                });
                this.bus.subscribeByType(RoomChangedMessage, (_msg) => {
                    this.stopScreenStreaming();
                    this.stopPlayer3DViewSharing();
                });
                // Relevant for Scripting / Clicks on the surface in the 3D World
                this.bus.subscribeByType(RequestStartScreenStreamMessage, (msg) => {
                    this.startScreenStreaming(msg.surfaceUid).catch((error) => {
                        if (!error.toString().includes("Permission denied")) {
                            console.error("[multiplayer] Error starting screen stream", error);
                        }
                        else {
                            const message = new ModelMediaStreamEndedMessage(msg.surfaceUid);
                            this.bus.send(message);
                        }
                    });
                });
                this.bus.subscribeByType(RequestStopScreenStreamMessage, (msg) => {
                    this.stopScreenStreaming(msg.surfaceUid);
                });
                this.bus.subscribeByType(RequestStartPlayer3DViewSharingMessage, (msg) => {
                    this.startPlayer3DViewSharing(msg.surfaceUid).catch((error) => {
                        console.error("[multiplayer] Error starting player 3d view sharing", error);
                        const message = new ModelMediaStreamEndedMessage(msg.surfaceUid);
                        this.bus.send(message);
                    });
                });
                this.bus.subscribeByType(RequestStopPlayer3DViewSharingMessage, (msg) => {
                    this.stopPlayer3DViewSharing(msg.surfaceUid);
                });
            }
            else {
                this.numberOfTriesToInitialize++;
                if (this.numberOfTriesToInitialize > 15) {
                    console.warn("[multiplayer] Couldn't initialize service after several attempts, trying again");
                    this.numberOfTriesToInitialize = 0;
                }
                setTimeout(doInit, 100);
            }
        };
        doInit();
        preferencesService.onPreferenceChanged.add((key, _newValue) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (key === "webcamPersonThreshold" || key === "webcamEdgeSoftness") {
                configureWebcamSegmentation(webcamBackgroundService);
                return true;
            }
            if (key === "webcamBlurRadius") {
                webcamBackgroundService.setBlurRadius((_a = preferencesService.getNumberPreferredValue("webcamBlurRadius", 9)) !== null && _a !== void 0 ? _a : 9);
                return true;
            }
            if (key !== "useWebcamBlur" && key !== "webcamBackgroundMode" && key !== "webcamBackgroundImageId")
                return false;
            if (!this.initialized || !useMediaDevicesStore.getState().cam.enabled || !window.location.search.includes("session"))
                return true;
            const revision = ++this.backgroundUpdateRevision;
            const update = this.backgroundUpdate.then(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (revision !== this.backgroundUpdateRevision || !useMediaDevicesStore.getState().cam.enabled)
                    return;
                if (this.cameraStream !== undefined && webcamBackgroundService.hasOutputFrame()) {
                    const source = this.cameraSourceStream;
                    yield configureWebcamBackground(webcamBackgroundService, () => this.cameraSourceStream === source && revision === this.backgroundUpdateRevision);
                    return;
                }
                if (this.cameraStream !== undefined && this.cameraStream === this.cameraSourceStream && getWebcamBackgroundMode() === "original")
                    return;
                this.stopCameraStreaming();
                yield this.startCameraStreaming((_a = useMediaDevicesStore.getState().cam.deviceId) !== null && _a !== void 0 ? _a : "");
            }));
            this.backgroundUpdate = update.catch((error) => {
                console.error("Error changing webcam background:", error);
                useMediaDevicesStore.getState().cam.setEnabled(false);
                MessageService.showMessage("warn", i18next.t("settings:warning-webcam-background-stream-failed", "The webcam background failed. Camera sharing has been stopped."));
            });
            yield update;
            return true;
        }));
    }
    /// Getter and setter ///
    getCameraDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            const devices = yield this.getStreamingDevices();
            return devices.filter((device) => device.kind === "videoinput");
        });
    }
    getMicrophoneDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            const devices = yield this.getStreamingDevices();
            return devices.filter((device) => device.kind === "audioinput");
        });
    }
    getSpeakerDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            const devices = yield this.getStreamingDevices();
            return devices.filter((device) => device.kind === "audiooutput");
        });
    }
    isReady() {
        return this.initialized;
    }
    /// Microphone stream ///
    startMicrophoneStreaming(deviceId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureInitialized();
            if (this.microphoneStream !== null && this.microphoneStream !== undefined) {
                this.stopStream(this.microphoneStream, "microphone");
                this.microphoneStream = undefined;
            }
            yield new Promise((resolve, reject) => {
                const constraints = {
                    audio: {
                        deviceId
                    }
                };
                this.startUserMedia(constraints, "microphone")
                    .then((stream) => {
                    this.microphoneStream = stream;
                    this.onUpdateDelegate.invoke();
                    resolve();
                })
                    .catch((error) => {
                    reject(error);
                });
            });
        });
    }
    stopMicrophoneStreaming() {
        this.ensureInitialized();
        if (this.microphoneStream != null) {
            this.stopStream(this.microphoneStream, "microphone");
            this.microphoneStream = undefined;
            this.onUpdateDelegate.invoke();
        }
    }
    getMicrophoneStream() {
        return this.microphoneStream;
    }
    /// Camera stream ///
    startCameraStreaming(deviceId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureInitialized();
            if (this.cameraStream !== null && this.cameraStream !== undefined) {
                this.stopStream(this.cameraStream, "camera");
                this.cameraStream = undefined;
            }
            yield new Promise((resolve, reject) => {
                const isLowEnd = capabilitiesService.isLowEndGPU || capabilitiesService.isMobile() || capabilitiesService.isTablet || capabilitiesService.isMobileVR;
                const constraints = {
                    video: isLowEnd
                        ? {
                            deviceId,
                            width: { max: 640 },
                            height: { max: 360 },
                            frameRate: { max: 15 }
                        }
                        : {
                            deviceId,
                            width: { ideal: 1280 },
                            height: { ideal: 720 }
                        }
                };
                this.startUserMedia(constraints, "camera")
                    .then(() => {
                    this.onUpdateDelegate.invoke();
                    adaptiveBandwidthService.start();
                    resolve();
                })
                    .catch((error) => {
                    reject(error);
                });
            });
        });
    }
    stopCameraStreaming() {
        var _a;
        this.ensureInitialized();
        webcamBackgroundService.stop();
        (_a = this.cameraSourceStream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => { track.stop(); });
        this.cameraSourceStream = undefined;
        if (this.cameraStream != null) {
            this.stopStream(this.cameraStream, "camera");
            this.cameraStream = undefined;
            this.onUpdateDelegate.invoke();
            adaptiveBandwidthService.stop();
        }
    }
    /// Player 3D stream ///
    startPlayer3DViewSharing(surfaceUid, sourceStream) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureInitialized();
            if (this.player3DViewStreams.find((player3DViewStream) => player3DViewStream.surfaceUid === surfaceUid) != null) {
                console.warn("[multiplayer] Player 3D view sharing already started for surface", surfaceUid);
                yield Promise.reject(new Error(`Screen streaming already started for surface ${surfaceUid}`));
                return;
            }
            yield new Promise((resolve, reject) => {
                if (renderer === undefined) {
                    reject(new Error("Renderer not available"));
                }
                try {
                    const stream = sourceStream !== null && sourceStream !== void 0 ? sourceStream : renderer.domElement.captureStream();
                    this.player3DViewStreams.push({ surfaceUid, stream });
                    // Relevant for Scripting / Clicks on the surface in the 3D World
                    const message = new ModelMediaStreamStartedMessage(surfaceUid, usersService.getLocalUser().clientId);
                    this.bus.send(message);
                    this.addStreamToPeers(stream, "player3dView", surfaceUid).catch((error) => {
                        console.error(`[multiplayer] Error adding player3dView stream to peers`, error);
                        reject(error);
                    });
                    this.onUpdateDelegate.invoke();
                    resolve();
                }
                catch (error) {
                    reject(error);
                }
            });
        });
    }
    stopPlayer3DViewSharing(surfaceUid) {
        this.ensureInitialized();
        if (surfaceUid !== undefined) {
            const player3DViewStream = this.player3DViewStreams.find((player3DViewStream) => player3DViewStream.surfaceUid === surfaceUid);
            if (player3DViewStream != null) {
                this.stopStream(player3DViewStream.stream, "player3dView");
                this.player3DViewStreams = this.player3DViewStreams.filter((player3DViewStream) => player3DViewStream.surfaceUid !== surfaceUid);
                // Relevant for Scripting / Clicks on the surface in the 3D World
                const message = new ModelMediaStreamEndedMessage(surfaceUid);
                this.bus.send(message);
                this.onUpdateDelegate.invoke();
            }
        }
        else {
            const modelMediaStreamEndedMessages = [];
            for (const player3DViewStream of this.player3DViewStreams) {
                modelMediaStreamEndedMessages.push(new ModelMediaStreamEndedMessage(player3DViewStream.surfaceUid));
                this.stopStream(player3DViewStream.stream, "player3dView");
            }
            this.bus.sendMultiple(modelMediaStreamEndedMessages);
            this.player3DViewStreams = [];
            this.onUpdateDelegate.invoke();
        }
    }
    hasConnectedPeers() {
        return this.connectedPeerIds.size > 0;
    }
    getPlayer3DViewStreams() {
        return this.player3DViewStreams;
    }
    /// Screen share stream ///
    startScreenStreaming(surfaceUid) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureInitialized();
            if (this.screenStreams.find((screenStream) => screenStream.surfaceUid === surfaceUid) != null) {
                console.warn("[multiplayer] Screen streaming already started for surface", surfaceUid);
                yield Promise.reject(new Error(`Screen streaming already started for surface ${surfaceUid}`));
                return;
            }
            yield new Promise((resolve, reject) => {
                var _a;
                (_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getDisplayMedia({ video: true }).then((stream) => {
                    this.screenStreams.push({ surfaceUid, stream });
                    // Relevant for Scripting / Clicks on the surface in the 3D World
                    const message = new ModelMediaStreamStartedMessage(surfaceUid, usersService.getLocalUser().clientId);
                    this.bus.send(message);
                    this.addStreamToPeers(stream, "screen", surfaceUid).catch((error) => {
                        console.error(`Error adding screen stream to peers`, error);
                    });
                    this.onUpdateDelegate.invoke();
                    resolve();
                }).catch((error) => {
                    console.error("[multiplayer] Error while starting screen share", error);
                    reject(error);
                });
            });
        });
    }
    stopScreenStreaming(surfaceUid) {
        this.ensureInitialized();
        if (surfaceUid !== undefined) {
            const screenStream = this.screenStreams.find((screenStream) => screenStream.surfaceUid === surfaceUid);
            if (screenStream != null) {
                this.stopStream(screenStream.stream, "screen");
                this.screenStreams = this.screenStreams.filter((screenStream) => screenStream.surfaceUid !== surfaceUid);
                // Relevant for Scripting / Clicks on the surface in the 3D World
                const message = new ModelMediaStreamEndedMessage(surfaceUid);
                this.bus.send(message);
                this.onUpdateDelegate.invoke();
            }
        }
        else {
            const modelMediaStreamEndedMessages = [];
            for (const screenStream of this.screenStreams) {
                this.stopStream(screenStream.stream, "screen");
                modelMediaStreamEndedMessages.push(new ModelMediaStreamEndedMessage(screenStream.surfaceUid));
            }
            this.bus.sendMultiple(modelMediaStreamEndedMessages);
            this.screenStreams = [];
            this.onUpdateDelegate.invoke();
        }
    }
    getScreenStreams() {
        return this.screenStreams;
    }
    /// Private peer stream methods ///
    addStreamToPeers(stream, type, surfaceUid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.peerMgr == null) {
                throw new Error("PeerManager not available");
            }
            yield Promise.all(Array.from(this.peerMgr.peers.values()).map((peer) => __awaiter(this, void 0, void 0, function* () {
                if (type === "microphone") {
                    yield peer.mediaConnections.setLocalAudioStream(stream);
                }
                else {
                    yield peer.mediaConnections.addLocalVideoStream(stream);
                }
                peer.sendReliableBusMessages([new MediaStreamTransmissionStartedMessage(stream.id, type, surfaceUid)]);
            })));
        });
    }
    sendStreamsToPeer(clientId) {
        var _a;
        this.ensureInitialized();
        if (this.cameraStream != null || this.microphoneStream != null || this.screenStreams.length > 0 || this.player3DViewStreams.length > 0) {
            const peer = (_a = this.peerMgr) === null || _a === void 0 ? void 0 : _a.getPeer(clientId);
            if (peer != null) {
                const camStream = this.cameraStream;
                if (camStream != null) {
                    void peer.mediaConnections.addLocalVideoStream(camStream).then(() => {
                        peer.sendReliableBusMessages([new MediaStreamTransmissionStartedMessage(camStream.id, "camera")]);
                    });
                }
                const micStream = this.microphoneStream;
                if (micStream != null) {
                    void peer.mediaConnections.setLocalAudioStream(micStream).then(() => {
                        peer.sendReliableBusMessages([new MediaStreamTransmissionStartedMessage(micStream.id, "microphone")]);
                    });
                }
                for (const screenStream of this.screenStreams) {
                    void peer.mediaConnections.addLocalVideoStream(screenStream.stream).then(() => {
                        peer.sendReliableBusMessages([new MediaStreamTransmissionStartedMessage(screenStream.stream.id, "screen", screenStream.surfaceUid)]);
                    });
                }
                for (const player3DViewStream of this.player3DViewStreams) {
                    void peer.mediaConnections.addLocalVideoStream(player3DViewStream.stream).then(() => {
                        peer.sendReliableBusMessages([
                            new MediaStreamTransmissionStartedMessage(player3DViewStream.stream.id, "player3dView", player3DViewStream.surfaceUid)
                        ]);
                    });
                }
            }
        }
    }
    stopStreamsToPeer(clientId) {
        var _a;
        this.ensureInitialized();
        const peer = (_a = this.peerMgr) === null || _a === void 0 ? void 0 : _a.getPeer(clientId);
        if (peer != null) {
            if (this.cameraStream != null) {
                peer.mediaConnections.removeLocalVideoStream(this.cameraStream);
            }
            if (this.microphoneStream != null) {
                peer.mediaConnections.removeLocalAudioStream();
            }
            for (const screenStream of this.screenStreams) {
                peer.mediaConnections.removeLocalVideoStream(screenStream.stream);
            }
            for (const player3DViewStream of this.player3DViewStreams) {
                peer.mediaConnections.removeLocalVideoStream(player3DViewStream.stream);
            }
        }
    }
    stopStream(stream, type) {
        var _a;
        this.ensureInitialized();
        stream.getTracks().forEach((track) => {
            track.stop();
        });
        (_a = this.peerMgr) === null || _a === void 0 ? void 0 : _a.peers.forEach((peer) => {
            if (type === "microphone") {
                peer.mediaConnections.removeLocalAudioStream();
            }
            else {
                peer.mediaConnections.removeLocalVideoStream(stream);
            }
        });
    }
    /// Helper methods ///
    ensureInitialized() {
        if (!this.initialized) {
            console.error("LocalStreamingService used before initialization");
            throw new Error("LocalStreamingService used before initialization");
        }
    }
    getStreamingDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!("mediaDevices" in navigator)) {
                const errorText = "LocalStreamingService.getStreamingDevices: navigator.mediaDevices is not defined";
                console.error(errorText);
                return [];
            }
            try {
                const devices = yield ((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.enumerateDevices());
                return devices !== null && devices !== void 0 ? devices : [];
            }
            catch (error) {
                console.error("Error getting streaming devices", error);
                return [];
            }
        });
    }
    startUserMedia(constraints, type) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ensureInitialized();
            return yield new Promise((resolve, reject) => {
                var _a;
                if (navigator.mediaDevices === undefined) {
                    console.error("Media Devices dont exist in a Web Worker context");
                    reject(new Error("Media Devices dont exist in a Web Worker context"));
                    return;
                }
                (_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia(constraints).then((stream) => __awaiter(this, void 0, void 0, function* () {
                    if (type === "camera") {
                        this.cameraSourceStream = stream;
                        const shouldUseBlur = getWebcamBackgroundMode() !== "original";
                        if (shouldUseBlur) {
                            webcamBackgroundService.onError = () => {
                                useMediaDevicesStore.getState().cam.setEnabled(false);
                                MessageService.showMessage("warn", i18next.t("settings:warning-webcam-background-stream-failed", "The webcam background failed. Camera sharing has been stopped."));
                            };
                            webcamBackgroundService.setVideoStream(stream);
                            try {
                                yield configureWebcamBackground(webcamBackgroundService, () => this.cameraSourceStream === stream);
                                if (this.cameraSourceStream !== stream)
                                    throw new Error("Camera background initialization was cancelled");
                                yield webcamBackgroundService.start();
                            }
                            catch (error) {
                                stream.getTracks().forEach((track) => { track.stop(); });
                                if (this.cameraSourceStream === stream)
                                    this.cameraSourceStream = undefined;
                                throw error;
                            }
                            this.cameraStream = webcamBackgroundService.getOutputStream();
                        }
                        else {
                            this.cameraStream = stream;
                            webcamBackgroundService.stop();
                        }
                        this.addStreamToPeers(this.cameraStream, type).catch((error) => {
                            console.error(`Error adding ${type} stream to peers`, error);
                        });
                        resolve(this.cameraStream);
                    }
                    else if (type === "microphone") {
                        this.addStreamToPeers(stream, type).catch((error) => {
                            console.error(`Error adding ${type} stream to peers`, error);
                        });
                        resolve(stream);
                    }
                })).catch((error) => {
                    console.error(`Error starting ${type} stream`, error);
                    reject(error);
                });
            });
        });
    }
}
const localStreamingService = new LocalStreamingService();
localStreamingService.init();
export { localStreamingService };
